/**
 * Custom field values as the chips a card shows.
 *
 * ── Why this is a lens and not a query shape ───────────────────────────────
 *
 * The database returns one row per value — task, field, option, label, colour.
 * A board needs the opposite: everything one card holds, in field order, with
 * a multi-select field's several values sitting together rather than scattered
 * among other fields' values.
 *
 * Doing that in the component would mean every card filtering the whole value
 * list, which is quadratic on a board of two hundred cards and is the sort of
 * thing that only shows up once the Social team has a real backlog in here.
 */

export interface FieldOption {
  id: string;
  label: string;
  color_key: string;
}

export interface FieldDefinition {
  field_id: string;
  name: string;
  sort_order: number;
  is_multi: boolean;
  options: FieldOption[];
}

export interface FieldValueRow {
  task_id: string;
  field_id: string;
  option_id: string;
  label: string;
  color_key: string;
}

export interface FieldChip {
  fieldId: string;
  optionId: string;
  label: string;
  colorKey: string;
}

/**
 * Every card's chips, in field order.
 *
 * Field order, not value order: a board where Format sometimes comes before
 * Platform and sometimes after is a board you have to read rather than scan.
 * Values arrive in whatever order the query returned them, so the definitions
 * are what impose the sequence.
 *
 * A value whose field is not in `definitions` is dropped. That happens when a
 * field is deactivated while somebody has the board open, and rendering a chip
 * from a field that is no longer on the board is worse than rendering nothing.
 */
export function chipsByTask(
  definitions: FieldDefinition[],
  values: FieldValueRow[],
): Map<string, FieldChip[]> {
  const rank = new Map<string, number>();
  definitions.forEach((definition, index) => rank.set(definition.field_id, index));

  const byTask = new Map<string, FieldChip[]>();
  for (const value of values) {
    if (!rank.has(value.field_id)) {
      continue;
    }
    const chips = byTask.get(value.task_id) ?? [];
    chips.push({
      fieldId: value.field_id,
      optionId: value.option_id,
      label: value.label,
      colorKey: value.color_key,
    });
    byTask.set(value.task_id, chips);
  }

  for (const chips of byTask.values()) {
    chips.sort((a, b) => {
      const byField = (rank.get(a.fieldId) ?? 0) - (rank.get(b.fieldId) ?? 0);
      // Within one multi-select field, alphabetical — so "Instagram, TikTok"
      // reads the same way on every card rather than in insertion order.
      return byField !== 0 ? byField : a.label.localeCompare(b.label);
    });
  }

  return byTask;
}

/** The option ids one task holds for one field. Drives the editor's checkmarks. */
export function selectedOptions(chips: FieldChip[], fieldId: string): Set<string> {
  return new Set(chips.filter((chip) => chip.fieldId === fieldId).map((chip) => chip.optionId));
}

/**
 * What clicking an option should do.
 *
 * Single-select: clicking the option already set CLEARS it, because "no
 * answer" is a real state and otherwise the only way back to it is a separate
 * control nobody finds. Clicking a different one replaces.
 *
 * Multi-select: clicking toggles that one option and leaves the rest alone.
 */
export function toggleAction(
  field: FieldDefinition,
  selected: Set<string>,
  optionId: string,
): { kind: 'set'; optionId: string } | { kind: 'clear'; optionId: string | null } {
  if (!selected.has(optionId)) {
    return { kind: 'set', optionId };
  }
  return { kind: 'clear', optionId: field.is_multi ? optionId : null };
}
